import { useState, useEffect } from "react";
import { getAuthHeader } from "../api/authToken.js";
import { useNavigate } from "react-router-dom";
import "../styles/App.css";

const HomePage = () => {
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [currentFloor, setCurrentFloor] = useState(7);
  const [roomsData, setRoomsData] = useState([]);
  const [roomCoordinates, setRoomCoordinates] = useState([]);
  const [filter, setFilter] = useState("all"); // Стани фільтру: "all", "free", "occupied"

  const maxFloor = 9;
  const minFloor = 1;

  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_BASE;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API}/api/rooms`, {
          headers: { Authorization: getAuthHeader() },
        });
        const data = await response.json();
        setRoomsData(data);
      } catch (error) {
        console.error("Error fetching rooms data:", error);
      }
    };

    const fetchCoordinatesData = async () => {
      try {
        const response = await fetch("/data/coordinates.json");
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        setRoomCoordinates(data);
      } catch (error) {
        console.error("Error loading coordinates data:", error);
      }
    };

    fetchData();
    fetchCoordinatesData();
  }, []);

  useEffect(() => {
    setSelectedRoom(null);
  }, [currentFloor]);

  const filteredRooms = Object.keys(roomCoordinates).filter((roomId) => {
    const roomFloor = parseInt(roomId.split("/")[0][0], 10);
    if (roomFloor !== currentFloor) return false;

    const room = roomsData.find((r) => r.id === roomId);

    if (filter === "free") {
      return !room || !room.occupants || room.occupants.length === 0;
    }
    if (filter === "occupied") {
      return room && room.occupants && room.occupants.length > 0;
    }
    return true; // "all"
  });

  const getRoomStyle = (roomId) => {
    const room = roomsData.find((r) => r.id === roomId);

    if (!room || !room.occupants || room.occupants.length === 0) {
      return { backgroundColor: "gray", color: "white" };
    }

    const maxViolations = Math.max(
      ...room.occupants.map((occupant) => occupant.violations || 0)
    );

    if (maxViolations >= 3)
      return {
        backgroundColor: "var(--full-busy)",
        color: "var(--main-color-2)",
      };
    if (maxViolations === 2)
      return {
        backgroundColor: "var(--half-busy)",
        color: "var(--main-color-2)",
      };
    if (maxViolations === 1)
      return { backgroundColor: "var(--half-busy)", color: "black" };
    return {
      backgroundColor: "var(--free-room)",
      color: "var(--main-color-2)",
    };
  };

  const handleRoomClick = (roomId) => {
    const room = roomsData.find((r) => r.id === roomId) || {
      id: roomId,
      beds: 0,
      occupants: [],
    };
    setSelectedRoom(room);
  };

  const handleFloorChange = (floor) => {
    setCurrentFloor(floor);
  };

  const handleStudentClick = (id) => {
    navigate(`/student/${encodeURIComponent(id)}`);
  };

  const countFreeBeds = (room) => {
    const occupiedBeds = room.occupants ? room.occupants.length : 0;
    return Math.max(0, room.beds - occupiedBeds);
  };

  const getStudentStyle = (violations) => {
    const backgroundColor =
      violations >= 3
        ? "var(--full-busy)"
        : violations === 2
        ? "var(--half-busy)"
        : violations === 1
        ? "var(--half-busy)"
        : "var(--free-room)";

    const color = backgroundColor === "var(--half-busy)" ? "black" : "white";
    return { backgroundColor, color };
  };

  return (
    <div className="page-container">
      <div className="controls-container">
        <div className="floor-controls">
          <img
            src={`./image/controls/arrowDown.png`}
            alt="⬇️"
            className="arrow"
            onClick={() =>
              setCurrentFloor((prev) => Math.max(minFloor, prev - 1))
            }
          />
          <select
            className="header-content-select"
            value={currentFloor}
            onChange={(e) => handleFloorChange(Number(e.target.value))}
          >
            {Array.from(
              { length: maxFloor - minFloor + 1 },
              (_, i) => minFloor + i
            ).map((floor) => (
              <option key={floor} value={floor}>
                {floor} поверх
              </option>
            ))}
          </select>
          <img
            src={`./image/controls/arrowUp.png`}
            alt="⬆️"
            className="arrow"
            onClick={() =>
              setCurrentFloor((prev) => Math.min(maxFloor, prev + 1))
            }
          />
        </div>

        <div className="filter-controls">
          <label>
            <input
              type="radio"
              name="filter"
              value="all"
              checked={filter === "all"}
              onChange={() => setFilter("all")}
            />
            Усі кімнати
          </label>
          <label>
            <input
              type="radio"
              name="filter"
              value="free"
              checked={filter === "free"}
              onChange={() => setFilter("free")}
            />
            Вільні кімнати
          </label>
          <label>
            <input
              type="radio"
              name="filter"
              value="occupied"
              checked={filter === "occupied"}
              onChange={() => setFilter("occupied")}
            />
            Зайняті кімнати
          </label>
        </div>
      </div>

      <div className="map-container">
        <img
          src={"./image/map.png"}
          alt={`Floor ${currentFloor} map`}
          className="map-image"
        />
        {filteredRooms.map((roomId) => (
          <div
            key={roomId}
            className="room-marker"
            style={{
              left: roomCoordinates[roomId]?.x,
              top: roomCoordinates[roomId]?.y,
              ...getRoomStyle(roomId),
            }}
            onClick={() => handleRoomClick(roomId)}
          >
            {roomId}
          </div>
        ))}

        {selectedRoom && (
          <div className="room-info">
            <div className="room-info-block">
              <h3 className="room-info-header">Кімната: {selectedRoom.id} </h3>
              <img
                src={`./image/controls/exitButton.png`}
                alt="Exit"
                className="room-info-header-image"
                onClick={() => setSelectedRoom(null)}
              />
            </div>

            <p>Кількість ліжок: {selectedRoom.beds}</p>
            <p>Вільні ліжка: {countFreeBeds(selectedRoom)}</p>
            <p className="room-info-container">
              Проживають:
              {selectedRoom.occupants && selectedRoom.occupants.length > 0 ? (
                <ul>
                  {selectedRoom.occupants.map((occupant) => (
                    <li
                      key={occupant.id}
                      style={{
                        ...getStudentStyle(occupant.violations),
                      }}
                      onClick={() => handleStudentClick(occupant.id)}
                    >
                      {occupant.name || "Невідомо"} - Акти:{" "}
                      {occupant.violations ?? "0"}
                    </li>
                  ))}
                </ul>
              ) : (
                " Нікого"
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;
